import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { CatalogGeoFacetOptionSchema, CatalogGeoFacetsSchema } from './geo.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * Transfer master (B4.10). Point-to-point transport extracted from the
 * legacy lumped Activity table. Pricing is per-vehicle (not per-pax) and is a
 * plain decimal amount in the row's currency; vehicle capacity + luggage
 * capacity are first-class fields.
 */

export const TransferTypeSchema = z.enum([
  'ONE_WAY',
  'ROUND_TRIP',
  'HOURLY',
  'DAY_HIRE',
  'MULTI_STOP',
]);
export type TransferType = z.infer<typeof TransferTypeSchema>;

/** What kind of transfer this is (vs TransferType, the trip shape). */
export const TransferCategorySchema = z.enum(['AIRPORT', 'HOTEL', 'INTERCITY', 'OTHER']);
export type TransferCategory = z.infer<typeof TransferCategorySchema>;

export const TransferStatusSchema = z.enum([
  'DRAFT',
  'PENDING_APPROVAL',
  'ACTIVE',
  'INACTIVE',
  'ARCHIVED',
]);
export type TransferStatus = z.infer<typeof TransferStatusSchema>;

export const WeekdaySchema = z.enum(['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']);
export type Weekday = z.infer<typeof WeekdaySchema>;

/**
 * Pricing basis for a transfer rate. PRIVATE = whole vehicle (priced per
 * vehicle, carries a vehicle type + capacity window). SIC = shared /
 * seat-in-coach (priced per person). A large-group coach is just a PRIVATE
 * rate with a Bus vehicle type and a high paxMax.
 */
export const TransferServiceTypeSchema = z.enum(['PRIVATE', 'SIC']);
export type TransferServiceType = z.infer<typeof TransferServiceTypeSchema>;

/**
 * Transport mode of a transfer (2026-08-02). CAR (default; every legacy row) is
 * the only mode with PRIVATE per-vehicle pricing + a vehicle type + driver
 * fields. BUS / TRAIN / FERRY are public, PER-PERSON modes: no vehicle/driver,
 * priced per seat/cabin by a fare class, with every rate stored as SIC. FERRY
 * additionally may span multiple nights.
 */
export const TransferModeSchema = z.enum(['CAR', 'BUS', 'TRAIN', 'FERRY']);
export type TransferMode = z.infer<typeof TransferModeSchema>;

/** BUS / TRAIN / FERRY - the per-person "public" modes (everything except CAR). */
export const isPublicTransferMode = (mode: TransferMode): boolean => mode !== 'CAR';

/** A money amount: non-negative number with up to 2 decimal places. */
const PriceSchema = z.number().nonnegative();

export const TransferPhotoSchema = z
  .object({
    /** Media-library asset id (preferred - survives presigned-URL expiry). */
    mediaId: z.string().uuid().optional(),
    /** Direct URL. Legacy imports only; new uploads store mediaId. */
    url: z.string().url().optional(),
    caption: z.string().max(200).nullable().optional(),
    alt: z.string().max(200).nullable().optional(),
    sortOrder: z.number().int().min(0).optional(),
  })
  .refine((p) => Boolean(p.mediaId || p.url), { message: 'Photo needs a mediaId or a url' });
export type TransferPhoto = z.infer<typeof TransferPhotoSchema>;
export const TransferPhotoListSchema = z.array(TransferPhotoSchema);

/**
 * A sellable rate on a transfer route (read shape). One transfer holds N of
 * these. PRIVATE rows use `pricePerVehicle` + a vehicle type + a pax/luggage
 * window; SIC rows use `perPersonPrice`. Rates store BASE COST only - markup
 * lives on the parent transfer.
 */
export const TransferRateSchema = z.object({
  id: UuidSchema,
  serviceType: TransferServiceTypeSchema,
  /** Per-rate plain-text one-liner (moved off the Transfer master). */
  shortDescription: z.string().nullable(),
  /** Per-rate multi-paragraph plain-text blurb. */
  longDescription: z.string().nullable(),
  vehicleTypeId: UuidSchema.nullable(),
  vehicleTypeName: z.string().nullable(),
  /** Fare class / cabin category for the public modes (BUS/TRAIN/FERRY); null for CAR. */
  fareClassName: z.string().nullable(),
  /** Days this rate is valid for once activated (multi-day travel passes: Swiss
   *  Travel Pass / JR Pass). Null for ordinary point-to-point rates. */
  validityDays: z.number().int().positive().nullable(),
  label: z.string(),
  paxMin: z.number().int(),
  paxMax: z.number().int(),
  maxLuggagePieces: z.number().int().nullable(),
  pricePerVehicle: z.number(),
  /** SIC per-seat ADULT price. */
  perPersonPrice: z.number(),
  /** SIC per-seat CHILD (age 2-8) price. Null/0 -> child pays the adult price;
   *  children older than 8 always pay the adult price (they need a full seat). */
  perPersonChildPrice: z.number().nullable(),
  minPaxToOperate: z.number().int().nullable(),
  isBase: z.boolean(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
});
export type TransferRate = z.infer<typeof TransferRateSchema>;

/** Write shape for a rate. `vehicleTypeName` is snapshotted server-side. */
export const TransferRateInputSchema = z
  .object({
    serviceType: TransferServiceTypeSchema.default('PRIVATE'),
    shortDescription: z.string().max(1000).nullable().optional(),
    longDescription: z.string().max(50_000).nullable().optional(),
    vehicleTypeId: UuidSchema.nullable().optional(),
    /** Fare class / cabin (public modes). Free text: "First class", "Balcony cabin". */
    fareClassName: z.string().trim().max(120).nullable().optional(),
    /** Days this rate is valid for once activated (multi-day travel passes). */
    validityDays: z.number().int().positive().nullable().optional(),
    label: z.string().trim().min(1).max(120),
    paxMin: z.number().int().min(1).max(200).default(1),
    paxMax: z.number().int().min(1).max(200).default(1),
    maxLuggagePieces: z.number().int().min(0).max(200).nullable().optional(),
    pricePerVehicle: PriceSchema.default(0),
    perPersonPrice: PriceSchema.default(0),
    /** SIC per-seat CHILD (age 2-8) price. Optional; null/0 -> adult price. */
    perPersonChildPrice: PriceSchema.nullable().optional(),
    minPaxToOperate: z.number().int().min(1).max(200).nullable().optional(),
    isBase: z.boolean().default(false),
    isActive: z.boolean().default(true),
    sortOrder: z.number().int().min(0).default(0),
  })
  .refine((r) => r.paxMax >= r.paxMin, {
    message: 'paxMax must be greater than or equal to paxMin',
    path: ['paxMax'],
  })
  .refine((r) => r.serviceType !== 'PRIVATE' || Boolean(r.vehicleTypeId), {
    message: 'PRIVATE rates need a vehicle type',
    path: ['vehicleTypeId'],
  });
export type TransferRateInput = z.infer<typeof TransferRateInputSchema>;

export const TransferListItemSchema = z.object({
  id: UuidSchema,
  legacyId: z.number().int().nullable(),
  legacyCode: z.string().nullable(),
  slug: z.string().nullable(),
  status: TransferStatusSchema,
  name: z.string(),
  transferMode: TransferModeSchema,
  transferType: TransferTypeSchema,
  transferCategory: TransferCategorySchema.nullable(),
  /** Transport brand for public modes (rail network / ferry operator / coach); null for CAR. */
  operatorName: z.string().nullable().default(null),
  /** Multi-day travel pass (Swiss Travel Pass / JR Pass): country-scoped
   *  (cityId null, countryId set), valid across every city of that country;
   *  one fare class holds several rates, each a different validityDays. */
  isMultiDayPass: z.boolean(),
  /** Global transfer: when true, the itinerary builder's add-transfer picker
   *  shows it for EVERY city (geography filter bypassed). Default false. */
  isGlobal: z.boolean().default(false),
  fromLocationName: z.string().nullable(),
  toLocationName: z.string().nullable(),
  cityId: UuidSchema.nullable(),
  cityName: z.string(),
  countryName: z.string(),
  vendorSupplierId: UuidSchema.nullable(),
  vendorSupplierName: z.string().nullable(),
  currencyId: UuidSchema.nullable(),
  currencyCode: z.string().nullable(),
  // Rate summary. The base is the first rate; `baseRatePrice` is its
  // pricePerVehicle (PRIVATE) or perPersonPrice (SIC).
  rateCount: z.number().int(),
  baseRatePrice: z.number(),
  /**
   * Base rate price bulk-converted to INR (whole rupees) using the live FX
   * rate at list-time. Same value as baseRatePrice when the row is already
   * priced in INR. Cached upstream by CurrencyConversionService.
   */
  baseRatePriceInr: z.number().default(0),
  baseRateServiceType: TransferServiceTypeSchema.nullable(),
  isApprovedByAdmin: z.boolean(),
  photoCount: z.number().int(),
  /**
   * Entries in the standalone gallery (`galleryImageMediaIds`), stored
   * separately from `photos`. Add the two for the row's real image total.
   */
  galleryCount: z.number().int().default(0),
  /** First photo URL for card thumbnails / day-plan covers. Null when no photos. */
  imageUrl: z.string().nullable().default(null),
  createdAt: IsoDateTimeSchema,
});
export type TransferListItem = z.infer<typeof TransferListItemSchema>;

export const TransferListQuerySchema = PaginationQuerySchema.extend({
  status: TransferStatusSchema.optional(),
  transferMode: TransferModeSchema.optional(),
  transferType: TransferTypeSchema.optional(),
  transferCategory: TransferCategorySchema.optional(),
  cityId: UuidSchema.optional(),
  countryId: UuidSchema.optional(),
  vendorSupplierId: UuidSchema.optional(),
  isApprovedByAdmin: BooleanQueryParamSchema.optional(),
  hasPhotos: BooleanQueryParamSchema.optional(),
  /**
   * Readiness filter, images half. Counts `photos` AND the standalone gallery
   * together - the same total the list Readiness chip shows - so "Without
   * images" returns exactly the rows badged "No images". `hasPhotos` is the
   * older, photos-only flag kept for the itinerary pickers.
   */
  hasImages: BooleanQueryParamSchema.optional(),
  /**
   * Readiness filter, fields half. `true` = rows the Readiness chip badges as
   * "N missing" (no priced rate); `false` = rows badged "Info complete".
   * Images are the other, independent half - filter those with `hasImages`;
   * the two combine.
   */
  missingInfo: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
  /**
   * When true AND cityId is set, widen the city-scope to every transfer in
   * that city's country. See ActivityListQuerySchema.expandToCountry for the
   * motivating case (all-in-Denpasar Bali inventory). Ignored when cityId is
   * absent.
   */
  expandToCountry: BooleanQueryParamSchema.optional(),
  /**
   * When true, global transfers (isGlobal = true) are included regardless of
   * the cityId / countryId filter - they surface for every city. Still gated to
   * ACTIVE + approved server-side. The itinerary builder's add-transfer picker
   * sets this; the admin master list does not.
   */
  includeGlobal: BooleanQueryParamSchema.optional(),
});
export type TransferListQuery = z.infer<typeof TransferListQuerySchema>;

export const TransferListResponseSchema = paginatedResponseSchema(TransferListItemSchema);
export type TransferListResponse = z.infer<typeof TransferListResponseSchema>;

/**
 * Facet options for the Transfers list filters. Only the countries and cities
 * actually referenced by (non-deleted) transfers - so the Country / City
 * filter dropdowns show real, in-use values rather than the whole geography
 * master, and grow automatically as transfers for new places are added.
 */
export const TransferFacetOptionSchema = CatalogGeoFacetOptionSchema;
export type TransferFacetOption = z.infer<typeof TransferFacetOptionSchema>;

export const TransferFacetsSchema = CatalogGeoFacetsSchema;
export type TransferFacets = z.infer<typeof TransferFacetsSchema>;

export const TransferDetailSchema = TransferListItemSchema.extend({
  /**
   * True when this record holds a captured edit waiting on an admin decision.
   * After a transfer's first approval a non-admin's edits go into
   * `pendingPayload` and the live columns are left alone - so a save that
   * looks like it did nothing actually went to the approval queue. The UI
   * needs this to say so instead of reporting a plain "updated".
   */
  awaitingApproval: z.boolean().default(false),
  /** When the captured edit was submitted; null when nothing is pending. */
  pendingSubmittedAt: IsoDateTimeSchema.nullable().default(null),
  legacyType: z.string().nullable(),
  // Multi-modal (BUS/TRAIN/FERRY) - null for CAR. transferMode + operatorName
  // are inherited from the list item above.
  vesselOrServiceName: z.string().nullable(),
  ferryNights: z.number().int().nullable(),
  boardBasis: z.string().nullable(),
  modeDetailsJson: z.unknown().nullable(),
  countryId: UuidSchema.nullable(),
  routeDistanceKm: z.number().nullable(),
  estimatedDurationMinutes: z.number().int(),
  // Scheduling clock-times (legacy free-text "HH:MM"); end derived from start + duration.
  pickupTime: z.string().nullable(),
  scheduleStartTime: z.string().nullable(),
  scheduleEndTime: z.string().nullable(),
  contractRef: z.string().nullable(),
  markupType: z.string().nullable(),
  markupValue: z.number(),
  isPickupIncluded: z.boolean(),
  isDropOffIncluded: z.boolean(),
  includesFuel: z.boolean(),
  includesToll: z.boolean(),
  includesDriverGratuity: z.boolean(),
  languageId: UuidSchema.nullable(),
  shortDescription: z.string().nullable(),
  longDescriptionHtml: z.string().nullable(),
  inclusionsHtml: z.string().nullable(),
  exclusionsHtml: z.string().nullable(),
  termsHtml: z.string().nullable(),
  cancellationHtml: z.string().nullable(),
  runOnWeekdays: z.array(WeekdaySchema),
  validityFrom: z.string().nullable(),
  validityTo: z.string().nullable(),
  photos: z.unknown().nullable(),
  /// Standalone gallery of up to 4 ordered media-asset ids (admin-only).
  galleryImageMediaIds: z.array(UuidSchema),
  isApprovedByModerator: z.boolean(),
  lastApprovedAt: IsoDateTimeSchema.nullable(),
  rating: z.number().int(),
  metaTagName: z.string().nullable(),
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
  // B4.13: per-route sellable rates (vehicle tiers + SIC).
  rates: z.array(TransferRateSchema),
});
export type TransferDetail = z.infer<typeof TransferDetailSchema>;

const TransferCoreFieldsSchema = z.object({
  status: TransferStatusSchema.optional(),
  legacyCode: z.string().trim().max(40).nullable().optional(),
  legacyType: z.string().trim().max(40).nullable().optional(),
  slug: z.string().trim().min(1).max(160).nullable().optional(),
  // Identity
  name: z.string().trim().min(1).max(300),
  transferMode: TransferModeSchema.optional(), // defaults to CAR server-side
  transferType: TransferTypeSchema.optional(),
  transferCategory: TransferCategorySchema.nullable().optional(),
  /** Multi-day travel pass (Swiss Travel Pass / JR Pass). Defaults to false
   *  server-side. See refineTransferMode for the pass-shape rules this gates. */
  isMultiDayPass: z.boolean().optional(),
  /** Global transfer: available in the itinerary builder for every city
   *  (geography filter bypassed). Defaults to false server-side. */
  isGlobal: z.boolean().optional(),
  // Route - free text from/to (legacy PickupPoint/DropOffPoint), no location master.
  fromLocationName: z.string().trim().max(255).nullable().optional(),
  toLocationName: z.string().trim().max(255).nullable().optional(),
  // Scheduling clock-times ("HH:MM"); end derived from start + duration on import.
  pickupTime: z.string().max(20).nullable().optional(),
  scheduleStartTime: z.string().max(20).nullable().optional(),
  scheduleEndTime: z.string().max(20).nullable().optional(),
  routeDistanceKm: z.number().min(0).max(10000).nullable().optional(),
  estimatedDurationMinutes: z
    .number()
    .int()
    .min(0)
    .max(60 * 24 * 30)
    .optional(),
  // Origin - city/country chosen via the FK pickers; the name snapshots are
  // filled server-side from the selected ids.
  cityId: UuidSchema.nullable().optional(),
  cityName: z.string().trim().max(120).optional(),
  countryId: UuidSchema.nullable().optional(),
  countryName: z.string().trim().max(120).optional(),
  // Vendor + commercial
  vendorSupplierId: UuidSchema.nullable().optional(),
  contractRef: z.string().trim().max(120).nullable().optional(),
  // Pricing - currency + markup only. Amounts + vehicle + capacity live on the
  // rates below.
  currencyId: UuidSchema.nullable().optional(),
  markupType: z.enum(['PERCENT', 'FLAT']).nullable().optional(),
  markupValue: PriceSchema.optional(),
  // Inclusions
  isPickupIncluded: z.boolean().optional(),
  isDropOffIncluded: z.boolean().optional(),
  includesFuel: z.boolean().optional(),
  includesToll: z.boolean().optional(),
  includesDriverGratuity: z.boolean().optional(),
  // Logistics
  languageId: UuidSchema.nullable().optional(), // driver language (CAR/BUS)
  // Multi-modal (BUS/TRAIN/FERRY). All null for CAR (enforced by the refine on
  // Create/Update below). `operatorName` = transport brand; `ferryNights` +
  // `boardBasis` are FERRY-only; `modeDetailsJson` is a free-form per-mode bag.
  operatorName: z.string().trim().max(200).nullable().optional(),
  vesselOrServiceName: z.string().trim().max(200).nullable().optional(),
  ferryNights: z.number().int().min(1).max(120).nullable().optional(),
  boardBasis: z.string().trim().max(120).nullable().optional(),
  modeDetailsJson: z.record(z.string(), z.unknown()).nullable().optional(),
  // Content
  shortDescription: z.string().max(1000).nullable().optional(),
  longDescriptionHtml: z.string().max(50_000).nullable().optional(),
  inclusionsHtml: z.string().max(20_000).nullable().optional(),
  exclusionsHtml: z.string().max(20_000).nullable().optional(),
  termsHtml: z.string().max(20_000).nullable().optional(),
  cancellationHtml: z.string().max(20_000).nullable().optional(),
  // Operational
  runOnWeekdays: z.array(WeekdaySchema).optional(),
  validityFrom: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  validityTo: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  // Media
  photos: TransferPhotoListSchema.nullable().optional(),
  /// Standalone gallery: up to 4 ordered media-asset ids (admin-only).
  /// Kept separate from `photos`. Omit to leave unchanged; `[]` clears.
  galleryImageMediaIds: z.array(UuidSchema).max(4).optional(),
  // Misc
  rating: z.number().int().min(0).max(5).optional(),
  metaTagName: z.string().trim().max(200).nullable().optional(),
  // B4.13: sellable rates. Replace-all when provided: sending `rates` swaps the
  // whole set; omitting it leaves existing rates untouched (partial-PATCH safe).
  rates: z.array(TransferRateInputSchema).optional(),
});

/**
 * Cross-field mode rules (only checked when the payload carries `transferMode`,
 * so partial PATCHes that don't touch the mode are unaffected):
 *   - public modes (BUS/TRAIN/FERRY) are per-person only -> no PRIVATE rate,
 *     no vehicle type on any rate;
 *   - `ferryNights` is FERRY-only.
 * Keeps the "public transport has no PRIVATE/SIC choice" invariant the UI
 * enforces from also holding at the API + bulk-import boundary.
 *
 * Also carries the multi-day-pass shape rules (gated on `isMultiDayPass ===
 * true`, so ordinary transfers are unaffected):
 *   - a pass is country-scoped only - no cityId;
 *   - a pass is a public per-person product - no CAR mode;
 *   - every rate on a pass needs a positive validityDays.
 */
function refineTransferMode(
  data: {
    transferMode?: TransferMode;
    ferryNights?: number | null;
    isMultiDayPass?: boolean;
    cityId?: string | null;
    rates?: ReadonlyArray<{
      serviceType?: TransferServiceType | null;
      vehicleTypeId?: string | null;
      validityDays?: number | null;
    }>;
  },
  ctx: z.RefinementCtx,
): void {
  const mode = data.transferMode;
  if (mode !== undefined) {
    if (isPublicTransferMode(mode)) {
      (data.rates ?? []).forEach((r, i) => {
        if (r.serviceType === 'PRIVATE') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['rates', i, 'serviceType'],
            message: `${mode} transfers are per-person - use SIC rates, not PRIVATE`,
          });
        }
        if (r.vehicleTypeId) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['rates', i, 'vehicleTypeId'],
            message: `${mode} transfers have no vehicle type`,
          });
        }
      });
    }
    if (mode !== 'FERRY' && data.ferryNights != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['ferryNights'],
        message: 'ferryNights applies only to FERRY transfers',
      });
    }
  }

  if (data.isMultiDayPass === true) {
    if (data.cityId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cityId'],
        message: 'A multi-day pass is country-scoped - it cannot carry a cityId',
      });
    }
    if (mode === 'CAR') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['transferMode'],
        message: 'A multi-day pass is per-person public transport - it cannot be CAR',
      });
    }
    (data.rates ?? []).forEach((r, i) => {
      if (!r.validityDays || r.validityDays <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['rates', i, 'validityDays'],
          message: 'Multi-day pass rates need a positive validityDays',
        });
      }
    });
  }
}

export const CreateTransferRequestSchema = TransferCoreFieldsSchema.extend({
  // The first rate is the mandatory base (holds the vehicle/fare + price).
  rates: z.array(TransferRateInputSchema).min(1, 'At least one rate (the base) is required'),
}).superRefine(refineTransferMode);
export type CreateTransferRequest = z.infer<typeof CreateTransferRequestSchema>;

export const UpdateTransferRequestSchema =
  TransferCoreFieldsSchema.partial().superRefine(refineTransferMode);
export type UpdateTransferRequest = z.infer<typeof UpdateTransferRequestSchema>;

export const TransferApprovalRequestSchema = z.object({
  tier: z.enum(['admin', 'moderator']),
  approve: z.boolean(),
});
export type TransferApprovalRequest = z.infer<typeof TransferApprovalRequestSchema>;
